import React from 'react';
import { Theme, ThemeOptions, ThemeProvider, createTheme, styled } from '@mui/material/styles';
import { TypographyOptions } from '@mui/material/styles/createTypography';

export const POD_CARD_TEXT = { body: 13, small: 12, title: 15, heading: 14 } as const;

const px = (size: number): string => `${size}px`;
const LABEL_SCALE = POD_CARD_TEXT.small / POD_CARD_TEXT.body;

const scopedThemes = new WeakMap<Theme, Theme>();

const typographyOverrides: TypographyOptions & { pxToRem: (size: number) => string } = {
  pxToRem: px,
  h1: { fontSize: px(22) },
  h2: { fontSize: px(20) },
  h3: { fontSize: px(18) },
  h4: { fontSize: px(16) },
  h5: { fontSize: px(POD_CARD_TEXT.title) },
  h6: { fontSize: px(POD_CARD_TEXT.heading) },
  subtitle1: { fontSize: px(POD_CARD_TEXT.heading) },
  subtitle2: { fontSize: px(POD_CARD_TEXT.body) },
  body1: { fontSize: px(POD_CARD_TEXT.body) },
  body2: { fontSize: px(POD_CARD_TEXT.body) },
  button: { fontSize: px(POD_CARD_TEXT.body) },
  caption: { fontSize: px(POD_CARD_TEXT.small) },
  overline: { fontSize: px(POD_CARD_TEXT.small) },
};

const componentOverrides: ThemeOptions['components'] = {
  MuiInputLabel: {
    styleOverrides: {
      shrink: {
        '&.MuiInputLabel-outlined': {
          transform: `translate(14px, -9px) scale(${LABEL_SCALE})`,
          maxWidth: `calc(${Math.ceil(100 / LABEL_SCALE)}% - 32px)`,
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      notchedOutline: { '& legend': { fontSize: px(POD_CARD_TEXT.small) } },
    },
  },
};

const outerOptions = (outer: Theme): ThemeOptions => outer as unknown as ThemeOptions;

export const podCardTheme = (outer: Theme): Theme => {
  const cached = scopedThemes.get(outer);
  if (cached) return cached;
  const built = createTheme(outerOptions(outer), {
    typography: typographyOverrides,
    components: componentOverrides,
  });
  scopedThemes.set(outer, built);
  return built;
};

const Scope = styled('div')({
  width: '100%',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

interface IProps {
  token?: string;
  children: React.ReactNode;
}

const AlphaPodCardScope: React.FC<IProps> = ({ token, children }) => (
  <ThemeProvider theme={podCardTheme}>
    <Scope data-alpha-scope="" data-alpha-card={token}>
      {children}
    </Scope>
  </ThemeProvider>
);

export default AlphaPodCardScope;
